import { useState, useEffect, useCallback } from 'react';
import {
  Users, Server, Shield, Activity, BarChart3,
  Building2, Globe, ScrollText, Settings,
  RefreshCw, Plus, Trash2, CheckCircle, XCircle,
  AlertTriangle, ChevronRight, Download, Search,
  Lock, Unlock, Power, PowerOff, Cloud, Mail, MailPlus,
} from 'lucide-react';
import { ConnectorsView } from '../connectors/ConnectorsView';
import { useTranslation } from 'react-i18next';
import { jsonInit, errorMessage, showAdminError } from './adminApi';
import { gt, uiLang } from './i18nUtil';

// --- API helper --------------------------------------------------------------
const API = (import.meta as { env: Record<string, string> }).env['VITE_API_URL'] ?? '/api';

async function apiGet<T>(path: string): Promise<T> {
  const token = localStorage.getItem('eurion_token') ?? '';
  const res = await fetch(`${API}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = (await res.json()) as { success: boolean; data: T };
  if (!json.success) throw new Error(String((json as { error?: { message?: string } }).error?.message));
  return json.data;
}

/** Every admin write goes through here: a failure is shown, never swallowed. */
async function apiCall(method: string, path: string, body?: unknown): Promise<{ success: boolean }> {
  try {
    const res = await fetch(`${API}${path}`, jsonInit(method, body));
    if (!res.ok) {
      showAdminError(await errorMessage(res));
      return { success: false };
    }
    const json = await res.json().catch(() => ({ success: true })) as { success?: boolean };
    return { success: json.success !== false };
  } catch (e) {
    showAdminError(gt('adminUi.networkError', 'Network error: {{msg}}', { msg: (e as Error).message }));
    return { success: false };
  }
}

// --- Types -------------------------------------------------------------------
interface DashStats {
  activeOrganizations: number;
  activeUsers: number;
  verifiedDomains: number;
  pendingReports: number;
  activeRetentionRules: number;
}

interface Org {
  id: string;
  name: string;
  slug: string;
  status: string;
  plan: string;
  max_users: number;
  primary_domain: string | null;
  created_at: string;
  verified_domains?: number;
  total_domains?: number;
}

interface User {
  id: string;
  email: string;
  display_name: string | null;
  org_id: string | null;
  roles: string[];
  status: string;
  email_verified: boolean;
  last_login: string | null;
  created_at: string;
}

interface Domain {
  id: string;
  org_id: string;
  domain: string;
  is_primary: boolean;
  is_verified: boolean;
  created_at: string;
}

interface ServiceReg {
  name: string;
  display_name: string;
  category: string;
  endpoint: string | null;
  port: number | null;
  description: string | null;
  status?: 'healthy' | 'degraded' | 'down' | 'unknown';
  responseMs?: number;
}

interface AuditLog {
  id: string;
  timestamp: string;
  service: string;
  action: string;
  actor_id: string;
  resource_type: string;
  resource_id: string;
  ip_address: string | null;
}

// --- Sidebar (labels are the English defaults for t('adminUi.nav.<id>')) -----
type Section = 'dashboard' | 'orgs' | 'users' | 'domains' | 'services' | 'audit' | 'settings' | 'integrations';

const NAV: { id: Section; label: string; icon: React.ElementType }[] = [
  { id: 'dashboard',    label: 'Dashboard',     icon: BarChart3 },
  { id: 'orgs',         label: 'Organizations', icon: Building2 },
  { id: 'users',        label: 'Users',         icon: Users },
  { id: 'domains',      label: 'Domains',       icon: Globe },
  { id: 'services',     label: 'Services',      icon: Server },
  { id: 'integrations', label: 'Integrations',  icon: Cloud },
  { id: 'audit',        label: 'Audit Logs',    icon: ScrollText },
  { id: 'settings',     label: 'Settings',      icon: Settings },
];

// --- Dashboard panel ---------------------------------------------------------
export function DashboardPanel() {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet<DashStats>('/v1/admin/dashboard')
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const cards = stats ? [
    { label: t('adminUi.dashboard.activeOrgs', 'Active Organizations'), value: stats.activeOrganizations,  icon: Building2, color: 'text-nord-8' },
    { label: t('adminUi.dashboard.activeUsers', 'Active Users'), value: stats.activeUsers,          icon: Users,     color: 'text-nord-14' },
    { label: t('adminUi.dashboard.verifiedDomains', 'Verified Domains'), value: stats.verifiedDomains,      icon: Globe,     color: 'text-nord-13' },
  ] : [];

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-nord-6 flex items-center gap-2">
        <BarChart3 className="h-5 w-5 text-nord-8" /> {t('adminUi.dashboard.title', 'Platform Dashboard')}
      </h2>

      {loading ? (
        <div className="text-nord-muted text-sm animate-pulse">{t('common.loading', 'Loading…')}</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {cards.map(c => {
            const Icon = c.icon;
            return (
              <div key={c.label} className="card space-y-2">
                <Icon className={`h-5 w-5 ${c.color}`} />
                <div className="text-2xl font-bold text-nord-6">{c.value}</div>
                <div className="text-sm text-nord-muted">{c.label}</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="card space-y-3">
          <h3 className="text-sm font-medium text-nord-6 flex items-center gap-2">
            <Shield className="h-4 w-4 text-nord-13" /> {t('adminUi.dashboard.complianceStatus', 'Compliance Status')}
          </h3>
          {[
            { name: 'GDPR',           ok: true,  label: t('adminUi.dashboard.compliant', 'Compliant') },
            { name: 'NIS2',           ok: true,  label: t('adminUi.dashboard.compliant', 'Compliant') },
            { name: 'ISO 27001',      ok: false, label: t('adminUi.dashboard.inProgress', 'In Progress') },
            { name: t('adminUi.dashboard.euDataResidency', 'EU Data Residency'), ok: true, label: t('adminUi.dashboard.enforced', 'Enforced') },
          ].map(it => (
            <div key={it.name} className="flex items-center justify-between text-sm">
              <span className="text-nord-4">{it.name}</span>
              <span className={it.ok ? 'badge badge-success' : 'badge badge-warning'}>{it.label}</span>
            </div>
          ))}
        </div>

        <div className="card space-y-3">
          <h3 className="text-sm font-medium text-nord-6 flex items-center gap-2">
            <Activity className="h-4 w-4 text-nord-14" /> {t('adminUi.dashboard.quickStats', 'Quick Stats')}
          </h3>
          {stats && [
            { label: t('adminUi.dashboard.pendingReports', 'Pending Reports'), value: stats.pendingReports },
            { label: t('adminUi.dashboard.activeRetentionRules', 'Active Retention Rules'), value: stats.activeRetentionRules },
          ].map(it => (
            <div key={it.label} className="flex items-center justify-between text-sm">
              <span className="text-nord-4">{it.label}</span>
              <span className="text-nord-6 font-medium">{it.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// --- Organizations panel ------------------------------------------------------
export function OrgsPanel() {
  const { t } = useTranslation();
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', primaryDomain: '', plan: 'standard', maxUsers: '50' });

  const load = useCallback(() => {
    setLoading(true);
    apiGet<{ items: Org[]; total: number }>(`/v1/admin/orgs?q=${encodeURIComponent(search)}&limit=30`)
      .then(d => { setOrgs(d.items); setTotal(d.total); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => { load(); }, [load]);

  async function deleteOrg(id: string) {
    if (!confirm(t('adminUi.orgs.confirmDelete', 'Soft-delete this organization?'))) return;
    await apiCall('DELETE', `/v1/admin/orgs/${id}`);
    load();
  }

  async function submitCreate() {
    await apiCall('POST', '/v1/admin/orgs', {
      ...form, maxUsers: parseInt(form.maxUsers),
    });
    setCreating(false);
    setForm({ name: '', slug: '', primaryDomain: '', plan: 'standard', maxUsers: '50' });
    load();
  }

  const STATUS_COLOR: Record<string, string> = {
    active: 'badge-success', suspended: 'badge-warning', deleted: 'badge-error',
  };
  const fieldLabel = {
    name:          t('adminUi.orgs.fieldName', 'Name'),
    slug:          t('adminUi.orgs.fieldSlug', 'Slug'),
    primaryDomain: t('adminUi.orgs.fieldPrimaryDomain', 'Primary domain'),
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-nord-6 flex items-center gap-2">
          <Building2 className="h-5 w-5 text-nord-8" /> {t('adminUi.orgs.title', 'Organizations')}
          <span className="text-sm font-normal text-nord-muted ml-1">({total})</span>
        </h2>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-nord-muted" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t('adminUi.searchPlaceholder', 'Search…')} aria-label={t('adminUi.orgs.search', 'Search organizations')}
              className="pl-7 pr-3 py-1.5 text-sm bg-nord-1 border border-nord-2 rounded text-nord-6 placeholder-nord-muted focus:outline-none focus:border-nord-8 w-48" />
          </div>
          <button onClick={() => setCreating(true)} className="btn btn-primary flex items-center gap-1 text-sm">
            <Plus className="h-3.5 w-3.5" /> {t('adminUi.orgs.new', 'New Org')}
          </button>
        </div>
      </div>

      {creating && (
        <div className="card space-y-3 border border-nord-8/40">
          <h3 className="text-sm font-medium text-nord-6">{t('adminUi.orgs.create', 'Create Organization')}</h3>
          <div className="grid grid-cols-2 gap-3">
            {(['name', 'slug', 'primaryDomain'] as const).map(f => (
              <div key={f}>
                <label className="text-xs text-nord-muted capitalize">{fieldLabel[f]}</label>
                <input value={form[f]} onChange={e => setForm(p => ({ ...p, [f]: e.target.value }))}
                  aria-label={fieldLabel[f]}
                  className="mt-0.5 w-full px-2 py-1.5 text-sm bg-nord-1 border border-nord-2 rounded text-nord-6 focus:outline-none focus:border-nord-8" />
              </div>
            ))}
            <div>
              <label className="text-xs text-nord-muted">{t('adminUi.orgs.plan', 'Plan')}</label>
              <select value={form.plan} onChange={e => setForm(p => ({ ...p, plan: e.target.value }))}
                className="mt-0.5 w-full px-2 py-1.5 text-sm bg-nord-1 border border-nord-2 rounded text-nord-6 focus:outline-none focus:border-nord-8">
                {['standard', 'professional', 'enterprise'].map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-nord-muted">{t('adminUi.orgs.maxUsers', 'Max Users')}</label>
              <input type="number" value={form.maxUsers} onChange={e => setForm(p => ({ ...p, maxUsers: e.target.value }))}
                aria-label={t('adminUi.orgs.maxUsersAria', 'Max users')}
                className="mt-0.5 w-full px-2 py-1.5 text-sm bg-nord-1 border border-nord-2 rounded text-nord-6 focus:outline-none focus:border-nord-8" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setCreating(false)} className="btn btn-ghost text-sm">{t('common.cancel', 'Cancel')}</button>
            <button onClick={submitCreate} className="btn btn-primary text-sm">{t('adminUi.create', 'Create')}</button>
          </div>
        </div>
      )}

      {loading ? <div className="text-nord-muted text-sm animate-pulse">{t('common.loading', 'Loading…')}</div> : (
        <div className="overflow-x-auto rounded border border-nord-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-nord-2 text-nord-muted text-xs uppercase tracking-wide">
                {[
                  t('adminUi.orgs.colName', 'Name'), t('adminUi.orgs.colSlug', 'Slug'), t('adminUi.orgs.plan', 'Plan'),
                  t('adminUi.orgs.colDomains', 'Domains'), t('adminUi.orgs.maxUsers', 'Max Users'), t('adminUi.colStatus', 'Status'), '',
                ].map((h, i) => (
                  <th key={i} className="px-3 py-2 text-left font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orgs.map(org => (
                <tr key={org.id} className="border-b border-nord-2/50 hover:bg-nord-1/40 transition-colors">
                  <td className="px-3 py-2 font-medium text-nord-6">{org.name}</td>
                  <td className="px-3 py-2 text-nord-muted font-mono text-xs">{org.slug}</td>
                  <td className="px-3 py-2 capitalize text-nord-4">{org.plan}</td>
                  <td className="px-3 py-2 text-nord-4">
                    {org.verified_domains ?? 0}/{org.total_domains ?? 0}
                  </td>
                  <td className="px-3 py-2 text-nord-4">{org.max_users}</td>
                  <td className="px-3 py-2">
                    <span className={`badge ${STATUS_COLOR[org.status] ?? 'badge-neutral'}`}>{org.status}</span>
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => deleteOrg(org.id)} title={t('common.delete', 'Delete')}
                      className="text-nord-muted hover:text-nord-11 transition-colors">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --- Users panel -------------------------------------------------------------
export function UsersPanel() {
  const { t, i18n } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    apiGet<{ items: User[]; total: number }>(`/v1/admin/users?q=${encodeURIComponent(search)}&limit=50`)
      .then(d => { setUsers(d.items); setTotal(d.total); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => { load(); }, [load]);

  // Mailboxes on the mail server (mail-service). Null until known; not shown when mail isn't set up.
  const [mail, setMail] = useState<{ configured: boolean; domains: string[]; mailboxes: string[] } | null>(null);
  const loadMail = useCallback(() => {
    apiGet<{ configured: boolean; domains: string[]; mailboxes: string[] }>('/v1/mail/admin/mailboxes')
      .then(setMail).catch(() => setMail(null));
  }, []);
  useEffect(() => { loadMail(); }, [loadMail]);
  const showMail = !!mail?.configured;
  const canHaveMailbox = (u: User) => !!mail?.domains.includes(u.email.split('@')[1]?.toLowerCase() ?? '');

  async function createMailbox(u: User) {
    const r = await apiCall('POST', '/v1/mail/admin/mailboxes', { userId: u.id, displayName: u.display_name ?? undefined });
    if (r.success) loadMail();
  }

  async function deleteMailbox(u: User) {
    const typed = prompt(t('adminUi.users.confirmDeleteMailbox', 'Deleting the mailbox deletes all mail in it. Type {{email}} to confirm.', { email: u.email }));
    if (typed === null) return;
    const r = await apiCall('DELETE', `/v1/mail/admin/mailboxes/${encodeURIComponent(u.email.toLowerCase())}`, { confirm: typed.trim().toLowerCase() });
    if (r.success) loadMail();
  }

  async function setStatus(id: string, status: string) {
    await apiCall('PUT', `/v1/admin/users/${id}/status`, { status });
    load();
  }

  async function killSessions(id: string) {
    if (!confirm(t('adminUi.users.confirmSignOut', 'Sign this user out of all devices?'))) return;
    await apiCall('DELETE', `/v1/admin/users/${id}/sessions`);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-nord-6 flex items-center gap-2">
          <Users className="h-5 w-5 text-nord-14" /> {t('adminUi.users.title', 'Users')}
          <span className="text-sm font-normal text-nord-muted ml-1">({total})</span>
        </h2>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-nord-muted" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('adminUi.searchPlaceholder', 'Search…')} aria-label={t('adminUi.users.search', 'Search users')}
              className="pl-7 pr-3 py-1.5 text-sm bg-nord-1 border border-nord-2 rounded text-nord-6 placeholder-nord-muted focus:outline-none focus:border-nord-8 w-48" />
          </div>
          <button onClick={load} className="btn btn-ghost p-1.5" title={t('adminUi.refresh', 'Refresh')} aria-label={t('adminUi.refresh', 'Refresh')}><RefreshCw className="h-4 w-4" /></button>
        </div>
      </div>

      {loading ? <div className="text-nord-muted text-sm animate-pulse">{t('common.loading', 'Loading…')}</div> : (
        <div className="overflow-x-auto rounded border border-nord-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-nord-2 text-nord-muted text-xs uppercase tracking-wide">
                {[
                  t('adminUi.users.colEmail', 'Email'), t('adminUi.users.colName', 'Name'), t('adminUi.users.colRoles', 'Roles'),
                  t('adminUi.colStatus', 'Status'), ...(showMail ? [t('adminUi.users.colMailbox', 'Mailbox')] : []),
                  t('adminUi.users.colLastLogin', 'Last Login'), t('adminUi.colActions', 'Actions'),
                ].map((h, i) => (
                  <th key={i} className="px-3 py-2 text-left font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-nord-2/50 hover:bg-nord-1/40 transition-colors">
                  <td className="px-3 py-2 text-nord-6 font-medium">{u.email}</td>
                  <td className="px-3 py-2 text-nord-4">{u.display_name ?? '—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 flex-wrap">
                      {(u.roles ?? []).map(r => (
                        <span key={r} className="px-1.5 py-0.5 text-2xs rounded bg-nord-2 text-nord-4">{r}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`badge ${u.status === 'active' ? 'badge-success' : u.status === 'suspended' ? 'badge-warning' : 'badge-error'}`}>
                      {u.status}
                    </span>
                  </td>
                  {showMail && (
                    <td className="px-3 py-2">
                      {mail!.mailboxes.includes(u.email.toLowerCase()) ? (
                        <span className="flex items-center gap-1.5">
                          <span className="badge badge-success flex items-center gap-1"><Mail className="h-3 w-3" aria-hidden="true" /> {t('adminUi.users.mailboxYes', 'Yes')}</span>
                          <button onClick={() => void deleteMailbox(u)} title={t('adminUi.users.deleteMailbox', 'Delete mailbox')} aria-label={t('adminUi.users.deleteMailboxFor', 'Delete the mailbox of {{email}}', { email: u.email })} className="text-nord-muted hover:text-nord-11">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ) : !canHaveMailbox(u) ? (
                        <span className="text-xs text-nord-muted" title={t('adminUi.users.mailboxOtherDomain', 'Eurion Mail is not set up for this domain')}>—</span>
                      ) : !u.email_verified ? (
                        <span className="text-xs text-nord-muted">{t('adminUi.users.mailboxUnverified', 'Address not verified')}</span>
                      ) : (
                        <button onClick={() => void createMailbox(u)} className="btn btn-ghost flex items-center gap-1 px-2 py-0.5 text-xs" aria-label={t('adminUi.users.createMailboxFor', 'Create a mailbox for {{email}}', { email: u.email })}>
                          <MailPlus className="h-3.5 w-3.5" aria-hidden="true" /> {t('adminUi.users.createMailbox', 'Create mailbox')}
                        </button>
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2 text-nord-muted text-xs">
                    {u.last_login ? new Date(u.last_login).toLocaleDateString(uiLang(i18n)) : t('adminUi.time.never', 'Never')}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2">
                      {u.status === 'active' ? (
                        <button onClick={() => setStatus(u.id, 'suspended')} title={t('adminUi.users.suspend', 'Suspend')} className="text-nord-13 hover:opacity-70">
                          <Lock className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <button onClick={() => setStatus(u.id, 'active')} title={t('adminUi.users.activate', 'Activate')} className="text-nord-14 hover:opacity-70">
                          <Unlock className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {/* "Reset MFA" is hidden until identity-service has MFA: the button had nothing to reset */}
                      <button onClick={() => killSessions(u.id)} title={t('adminUi.users.signOutEverywhere', 'Sign out everywhere (ends refresh sessions; open sessions end when their access token expires)')} className="text-nord-muted hover:text-nord-11">
                        <XCircle className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --- Domains panel ------------------------------------------------------------
export function DomainsPanel() {
  const { t } = useTranslation();
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(false);
  const [newDomain, setNewDomain] = useState('');

  useEffect(() => {
    apiGet<{ items: Org[] }>('/v1/admin/orgs?limit=200').then(d => {
      setOrgs(d.items);
      if (d.items.length > 0) setSelectedOrg(d.items[0]!.id);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedOrg) return;
    setLoading(true);
    apiGet<Domain[]>(`/v1/admin/orgs/${selectedOrg}/domains`)
      .then(setDomains).catch(console.error).finally(() => setLoading(false));
  }, [selectedOrg]);

  async function addDomain() {
    if (!newDomain.trim()) return;
    await apiCall('POST', `/v1/admin/orgs/${selectedOrg}/domains`, { domain: newDomain.trim() });
    setNewDomain('');
    const fresh = await apiGet<Domain[]>(`/v1/admin/orgs/${selectedOrg}/domains`);
    setDomains(fresh);
  }

  async function verifyDomain(id: string) {
    await apiCall('POST', `/v1/admin/orgs/${selectedOrg}/domains/${id}/verify`);
    const fresh = await apiGet<Domain[]>(`/v1/admin/orgs/${selectedOrg}/domains`);
    setDomains(fresh);
  }

  async function deleteDomain(id: string) {
    if (!confirm(t('adminUi.domains.confirmRemove', 'Remove domain?'))) return;
    await apiCall('DELETE', `/v1/admin/orgs/${selectedOrg}/domains/${id}`);
    const fresh = await apiGet<Domain[]>(`/v1/admin/orgs/${selectedOrg}/domains`);
    setDomains(fresh);
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-nord-6 flex items-center gap-2">
        <Globe className="h-5 w-5 text-nord-13" /> {t('adminUi.domains.title', 'Domain Management')}
      </h2>

      <div className="flex items-center gap-3">
        <label className="text-sm text-nord-muted">{t('adminUi.organization', 'Organization')}</label>
        <select value={selectedOrg} onChange={e => setSelectedOrg(e.target.value)}
          className="px-2 py-1.5 text-sm bg-nord-1 border border-nord-2 rounded text-nord-6 focus:outline-none focus:border-nord-8">
          {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>

      <div className="flex gap-2">
        <input value={newDomain} onChange={e => setNewDomain(e.target.value)}
          placeholder="example.com" aria-label={t('adminUi.domains.new', 'New domain')}
          className="flex-1 px-2 py-1.5 text-sm bg-nord-1 border border-nord-2 rounded text-nord-6 focus:outline-none focus:border-nord-8" />
        <button onClick={addDomain} className="btn btn-primary flex items-center gap-1 text-sm">
          <Plus className="h-3.5 w-3.5" /> {t('adminUi.add', 'Add')}
        </button>
      </div>

      {loading ? <div className="text-nord-muted text-sm animate-pulse">{t('common.loading', 'Loading…')}</div> : (
        <div className="space-y-2">
          {domains.map(d => (
            <div key={d.id} className="card flex items-center justify-between py-2.5 px-3">
              <div className="flex items-center gap-3">
                {d.is_verified
                  ? <CheckCircle className="h-4 w-4 text-nord-14" />
                  : <AlertTriangle className="h-4 w-4 text-nord-13" />}
                <span className="text-nord-6 font-mono text-sm">{d.domain}</span>
                {d.is_primary && <span className="badge badge-success text-xs">{t('adminUi.domains.primary', 'primary')}</span>}
                {!d.is_verified && <span className="badge badge-warning text-xs">{t('adminUi.domains.unverified', 'unverified')}</span>}
              </div>
              <div className="flex gap-2">
                {!d.is_verified && (
                  <button onClick={() => verifyDomain(d.id)} className="text-xs text-nord-8 hover:underline">
                    {t('adminUi.domains.verify', 'Verify')}
                  </button>
                )}
                <button onClick={() => deleteDomain(d.id)} className="text-nord-muted hover:text-nord-11" title={t('adminUi.domains.remove', 'Remove domain')} aria-label={t('adminUi.domains.remove', 'Remove domain')}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
          {domains.length === 0 && <p className="text-nord-muted text-sm">{t('adminUi.domains.empty', 'No domains registered.')}</p>}
        </div>
      )}
    </div>
  );
}

// --- Services panel -----------------------------------------------------------
export function ServicesPanel() {
  const { t } = useTranslation();
  const [services, setServices] = useState<(ServiceReg & { is_enabled?: boolean })[]>([]);
  const [health, setHealth] = useState<Record<string, ServiceReg>>({});
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [selectedOrg, setSelectedOrg] = useState('');

  useEffect(() => {
    Promise.all([
      apiGet<ServiceReg[]>('/v1/admin/services'),
      apiGet<{ items: Org[] }>('/v1/admin/orgs?limit=200'),
    ]).then(([svcs, orgData]) => {
      setServices(svcs);
      setOrgs(orgData.items);
      if (orgData.items[0]) setSelectedOrg(orgData.items[0].id);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedOrg) return;
    apiGet<(ServiceReg & { is_enabled: boolean })[]>(`/v1/admin/orgs/${selectedOrg}/services`)
      .then(svcs => {
        const map: Record<string, boolean> = {};
        svcs.forEach(s => { map[s.name] = s.is_enabled; });
        setServices(prev => prev.map(s => ({ ...s, is_enabled: map[s.name] ?? false })));
      }).catch(console.error);
  }, [selectedOrg]);

  async function checkHealth() {
    setLoadingHealth(true);
    apiGet<{ services: ServiceReg[] }>('/v1/admin/services/health')
      .then(d => {
        const map: Record<string, ServiceReg> = {};
        d.services.forEach(s => { map[s.name] = s; });
        setHealth(map);
      }).catch(console.error)
      .finally(() => setLoadingHealth(false));
  }

  async function toggleService(name: string, current: boolean) {
    await apiCall('PUT', `/v1/admin/orgs/${selectedOrg}/services/${name}`, { isEnabled: !current });
    setServices(prev => prev.map(s => s.name === name ? { ...s, is_enabled: !current } : s));
  }

  const HEALTH_ICON: Record<string, React.ReactNode> = {
    healthy:  <CheckCircle className="h-3.5 w-3.5 text-nord-14" />,
    degraded: <AlertTriangle className="h-3.5 w-3.5 text-nord-13" />,
    down:     <XCircle className="h-3.5 w-3.5 text-nord-11" />,
    unknown:  <Activity className="h-3.5 w-3.5 text-nord-muted" />,
  };

  const byCategory = services.reduce<Record<string, typeof services>>((acc, s) => {
    (acc[s.category] = acc[s.category] ?? []).push(s);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-nord-6 flex items-center gap-2">
          <Server className="h-5 w-5 text-nord-15" /> {t('adminUi.services.title', 'Services')}
        </h2>
        <div className="flex items-center gap-3">
          <label className="text-sm text-nord-muted">{t('adminUi.services.org', 'Org')}</label>
          <select value={selectedOrg} onChange={e => setSelectedOrg(e.target.value)}
            className="px-2 py-1.5 text-sm bg-nord-1 border border-nord-2 rounded text-nord-6 focus:outline-none focus:border-nord-8">
            {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <button onClick={checkHealth} disabled={loadingHealth}
            className="btn btn-ghost flex items-center gap-1 text-sm">
            <RefreshCw className={`h-3.5 w-3.5 ${loadingHealth ? 'animate-spin' : ''}`} />
            {t('adminUi.services.checkHealth', 'Check Health')}
          </button>
        </div>
      </div>

      {Object.entries(byCategory).map(([cat, svcs]) => (
        <div key={cat} className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-nord-muted px-1">{cat}</h3>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {svcs.map(s => {
              const h = health[s.name];
              return (
                <div key={s.name} className="card flex items-center justify-between py-2.5 px-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {h ? HEALTH_ICON[h.status ?? 'unknown'] : <div className="h-3.5 w-3.5 rounded-full bg-nord-2" />}
                      <span className="text-nord-6 text-sm font-medium truncate">{s.display_name}</span>
                    </div>
                    {h?.responseMs != null && (
                      <span className="text-2xs text-nord-muted ml-5">{h.responseMs}ms</span>
                    )}
                  </div>
                  <button onClick={() => toggleService(s.name, s.is_enabled ?? false)}
                    title={s.is_enabled ? t('adminUi.services.disable', 'Disable') : t('adminUi.services.enable', 'Enable')}
                    className={s.is_enabled ? 'text-nord-14 hover:opacity-70' : 'text-nord-muted hover:text-nord-14'}>
                    {s.is_enabled ? <Power className="h-4 w-4" /> : <PowerOff className="h-4 w-4" />}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Audit logs panel ---------------------------------------------------------
export function AuditPanel() {
  const { t, i18n } = useTranslation();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ service: '', action: '', q: '' });
  const [filterOpts, setFilterOpts] = useState<{ services: string[]; actions: string[] }>({ services: [], actions: [] });

  useEffect(() => {
    apiGet<{ services: string[]; actions: string[] }>('/v1/admin/audit/filters')
      .then(setFilterOpts).catch(console.error);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ limit: '100' });
    if (filters.service) p.set('service', filters.service);
    if (filters.action)  p.set('action',  filters.action);
    if (filters.q)       p.set('q',       filters.q);
    apiGet<{ items: AuditLog[]; total: number }>(`/v1/admin/audit?${p}`)
      .then(d => { setLogs(d.items); setTotal(d.total); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  function exportLogs() {
    const token = localStorage.getItem('eurion_token') ?? '';
    const p = new URLSearchParams();
    if (filters.service) p.set('service', filters.service);
    if (filters.action)  p.set('action',  filters.action);
    window.open(`${API}/v1/admin/audit/export?${p}&token=${token}`, '_blank');
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-nord-6 flex items-center gap-2">
          <ScrollText className="h-5 w-5 text-nord-8" /> {t('adminUi.audit.title', 'Audit Logs')}
          <span className="text-sm font-normal text-nord-muted ml-1">({total})</span>
        </h2>
        <div className="flex gap-2 items-center flex-wrap">
          <select value={filters.service} onChange={e => setFilters(p => ({ ...p, service: e.target.value }))}
            className="px-2 py-1.5 text-sm bg-nord-1 border border-nord-2 rounded text-nord-6 focus:outline-none focus:border-nord-8">
            <option value="">{t('adminUi.audit.allServices', 'All services')}</option>
            {filterOpts.services.map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={filters.action} onChange={e => setFilters(p => ({ ...p, action: e.target.value }))}
            className="px-2 py-1.5 text-sm bg-nord-1 border border-nord-2 rounded text-nord-6 focus:outline-none focus:border-nord-8">
            <option value="">{t('adminUi.audit.allActions', 'All actions')}</option>
            {filterOpts.actions.map(a => <option key={a}>{a}</option>)}
          </select>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-nord-muted" />
            <input value={filters.q} onChange={e => setFilters(p => ({ ...p, q: e.target.value }))} placeholder={t('adminUi.searchPlaceholder', 'Search…')} aria-label={t('adminUi.audit.search', 'Search audit logs')}
              className="pl-7 pr-3 py-1.5 text-sm bg-nord-1 border border-nord-2 rounded text-nord-6 placeholder-nord-muted focus:outline-none focus:border-nord-8 w-40" />
          </div>
          <button onClick={exportLogs} className="btn btn-ghost flex items-center gap-1 text-sm">
            <Download className="h-3.5 w-3.5" /> {t('adminUi.audit.export', 'Export')}
          </button>
        </div>
      </div>

      {loading ? <div className="text-nord-muted text-sm animate-pulse">{t('common.loading', 'Loading…')}</div> : (
        <div className="overflow-x-auto rounded border border-nord-2">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-nord-2 text-nord-muted uppercase tracking-wide">
                {[
                  t('adminUi.audit.colTime', 'Time'), t('adminUi.audit.colService', 'Service'), t('adminUi.audit.colAction', 'Action'),
                  t('adminUi.audit.colActor', 'Actor'), t('adminUi.audit.colResource', 'Resource'), t('adminUi.audit.colIp', 'IP'),
                ].map((h, i) => (
                  <th key={i} className="px-3 py-2 text-left font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map(l => (
                <tr key={l.id} className="border-b border-nord-2/50 hover:bg-nord-1/40 transition-colors">
                  <td className="px-3 py-1.5 text-nord-muted font-mono whitespace-nowrap">
                    {new Date(l.timestamp).toLocaleString(uiLang(i18n))}
                  </td>
                  <td className="px-3 py-1.5 text-nord-4">{l.service}</td>
                  <td className="px-3 py-1.5 font-medium text-nord-6">{l.action}</td>
                  <td className="px-3 py-1.5 text-nord-muted font-mono text-2xs truncate max-w-[120px]">{l.actor_id}</td>
                  <td className="px-3 py-1.5 text-nord-4">{l.resource_type}/{l.resource_id}</td>
                  <td className="px-3 py-1.5 text-nord-muted font-mono">{l.ip_address ?? '—'}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-4 text-center text-nord-muted">{t('adminUi.audit.empty', 'No logs found.')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --- Settings panel -----------------------------------------------------------
export { type SecPolicy };
interface SecPolicy {
  mfa_required: boolean;
  min_password_length: number;
  password_require_uppercase: boolean;
  password_require_numbers: boolean;
  password_require_symbols: boolean;
  session_timeout_minutes: number;
  max_login_attempts: number;
  lockout_duration_minutes: number;
  allowed_ip_ranges?: string[] | null;
  /** People may share files by links that work without signing in */
  allow_public_links: boolean;
  /** Eurion Mail creates a person's mailbox the first time they open Mail */
  auto_create_mailboxes: boolean;
}

export function SettingsPanel() {
  const { t } = useTranslation();
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [policy, setPolicy] = useState<SecPolicy | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    apiGet<{ items: Org[] }>('/v1/admin/orgs?limit=200').then(d => {
      setOrgs(d.items);
      if (d.items[0]) setSelectedOrg(d.items[0].id);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedOrg) return;
    apiGet<SecPolicy>(`/v1/admin/orgs/${selectedOrg}/security`).then(setPolicy).catch(console.error);
  }, [selectedOrg]);

  async function save() {
    if (!policy) return;
    setSaving(true);
    setSaved(false);
    const r = await apiCall('PUT', `/v1/admin/orgs/${selectedOrg}/security`, {
      mfaRequired:              policy.mfa_required,
      minPasswordLength:        policy.min_password_length,
      passwordRequireUppercase: policy.password_require_uppercase,
      passwordRequireNumbers:   policy.password_require_numbers,
      passwordRequireSymbols:   policy.password_require_symbols,
      sessionTimeoutMinutes:    policy.session_timeout_minutes,
      maxLoginAttempts:         policy.max_login_attempts,
      lockoutDurationMinutes:   policy.lockout_duration_minutes,
      // the column is NOT NULL: always send the list (empty = no IP restriction)
      allowedIpRanges:          policy.allowed_ip_ranges ?? [],
      allowPublicLinks:         policy.allow_public_links,
      autoCreateMailboxes:      policy.auto_create_mailboxes,
    }).finally(() => setSaving(false));
    if (r.success) { setSaved(true); setTimeout(() => setSaved(false), 3000); }
  }

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-nord-6 flex items-center gap-2">
        <Settings className="h-5 w-5 text-nord-15" /> {t('adminUi.security.title', 'Security Policy')}
      </h2>

      <div className="flex items-center gap-3">
        <label className="text-sm text-nord-muted">{t('adminUi.organization', 'Organization')}</label>
        <select value={selectedOrg} onChange={e => setSelectedOrg(e.target.value)}
          className="px-2 py-1.5 text-sm bg-nord-1 border border-nord-2 rounded text-nord-6 focus:outline-none focus:border-nord-8">
          {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>

      {policy && (
        <div className="card space-y-5 max-w-lg">

          {/* Toggles */}
          <div className="space-y-3">
            {([
              ['mfa_required',               t('adminUi.security.requireMfa', 'Require MFA')],
              ['password_require_uppercase', t('adminUi.security.requireUppercase', 'Require uppercase letters')],
              ['password_require_numbers',   t('adminUi.security.requireNumbers', 'Require numbers')],
              ['password_require_symbols',   t('adminUi.security.requireSymbols', 'Require symbols')],
              ['allow_public_links',         t('adminUi.security.allowPublicLinks', 'Allow public share links (no sign-in, expire after at most 90 days)')],
              ['auto_create_mailboxes',      t('adminUi.security.autoCreateMailboxes', 'Create mailboxes automatically (the first time someone with a verified address opens Mail)')],
            ] as [keyof SecPolicy, string][]).map(([key, label]) => (
              <label key={key} className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-nord-4">{label}</span>
                <button
                  onClick={() => setPolicy(p => p ? { ...p, [key]: !p[key] } : p)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${policy[key] ? 'bg-nord-8' : 'bg-nord-2'}`}>
                  <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transform transition-transform ${policy[key] ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </label>
            ))}
          </div>

          {/* Numeric inputs */}
          <div className="grid grid-cols-2 gap-4">
            {([
              ['min_password_length',      t('adminUi.security.minPasswordLength', 'Min password length')],
              ['session_timeout_minutes',  t('adminUi.security.sessionTimeout', 'Session timeout (min)')],
              ['max_login_attempts',       t('adminUi.security.maxLoginAttempts', 'Max login attempts')],
              ['lockout_duration_minutes', t('adminUi.security.lockoutDuration', 'Lockout duration (min)')],
            ] as [keyof SecPolicy, string][]).map(([key, label]) => (
              <div key={key}>
                <label className="text-xs text-nord-muted">{label}</label>
                <input
                  type="number"
                  value={policy[key] as number}
                  onChange={e => setPolicy(p => p ? { ...p, [key]: parseInt(e.target.value) } : p)}
                  className="mt-0.5 w-full px-2 py-1.5 text-sm bg-nord-0 border border-nord-2 rounded text-nord-6 focus:outline-none focus:border-nord-8"
                />
              </div>
            ))}
          </div>

          <button onClick={save} disabled={saving}
            className="btn btn-primary w-full flex items-center justify-center gap-2">
            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
            {saving ? t('adminUi.saving', 'Saving…') : saved ? t('adminUi.security.saved', 'Saved ✓') : t('adminUi.security.save', 'Save Policy')}
          </button>
        </div>
      )}
    </div>
  );
}

// --- Root AdminView -----------------------------------------------------------
export function AdminView() {
  const { t } = useTranslation();
  const [section, setSection] = useState<Section>('dashboard');

  const PANELS: Record<Section, React.ReactNode> = {
    dashboard:    <DashboardPanel />,
    orgs:         <OrgsPanel />,
    users:        <UsersPanel />,
    domains:      <DomainsPanel />,
    services:     <ServicesPanel />,
    integrations: <ConnectorsView />,
    audit:        <AuditPanel />,
    settings:     <SettingsPanel />,
  };

  return (
    <div className="flex h-full overflow-hidden">
      {/* Sidebar */}
      <nav className="w-48 shrink-0 flex flex-col border-r border-nord-2 bg-nord-0 py-4 overflow-y-auto">
        <div className="px-4 mb-4">
          <span className="text-xs font-semibold uppercase tracking-wider text-nord-muted">{t('adminUi.nav.admin', 'Admin')}</span>
        </div>
        {NAV.map(n => {
          const Icon = n.icon;
          const active = section === n.id;
          return (
            <button
              key={n.id}
              onClick={() => setSection(n.id)}
              className={`flex items-center gap-2.5 px-4 py-2 text-sm transition-colors text-left
                ${active
                  ? 'bg-nord-2 text-nord-6 font-medium border-r-2 border-nord-8'
                  : 'text-nord-4 hover:bg-nord-1 hover:text-nord-6'}`}>
              <Icon className="h-4 w-4 shrink-0" />
              {t(`adminUi.nav.${n.id}`, n.label)}
              {active && <ChevronRight className="h-3 w-3 ml-auto text-nord-muted" />}
            </button>
          );
        })}
      </nav>

      {/* Content */}
      <main className="flex-1 overflow-y-auto p-6">
        {PANELS[section]}
      </main>
    </div>
  );
}
